/**
 * Task 129 D1/D2 — shared helpers for turning a `/workblocks` day
 * response into render-ready block positions.
 *
 * This module is the single source of truth for:
 *   - The deterministic `projectId → colour-token` map used across both
 *     the Range and Daily pages (so the same project draws the same
 *     colour on every page).
 *   - The `buildDayBlocksFromWorkblocks` layout helper that produces
 *     `DayBlock[]` from a day's `WorkBlocksDayResponse`.
 *   - The `toTimelineBlock` adapter that adapts a `DayBlock` to the
 *     `TimelineBlock` shape the shared `<DayTimeline>` component consumes.
 *
 * Task 128 originally defined `buildDayBlocksFromWorkblocks` inline in
 * TimesheetRangePage.tsx. Task 129 lifts it here so the Daily page can
 * reuse it without the two implementations drifting apart.
 */
import type { WorkBlock as WorkBlockView, WorkBlocksDayResponse } from '@/types/dashboard';
import type { TimelineBlock } from '@/types/timeline';

/* ── Project colour rotation (deterministic per page render) ────────── */

export const PROJECT_COLORS = [
  '--color-project-a',
  '--color-project-b',
  '--color-project-c',
] as const;
export const PROJECT_COLOR_FALLBACK = PROJECT_COLORS[0];

/** Safe modular pick into PROJECT_COLORS — avoids non-null assertions at
 *  every call site while preserving the rotation semantics. */
export function projectColorAt(slot: number): string {
  return PROJECT_COLORS[slot % PROJECT_COLORS.length] ?? PROJECT_COLOR_FALLBACK;
}

/**
 * Returns a `(projectId | null) → colour-token` function that assigns
 * slots in insertion order. Unclassified blocks (projectId `null`) are
 * NOT given a slot — they collapse onto the fallback token and render
 * with reduced opacity via the `timelineBlockUnclassified` CSS rule
 * (handled in the consuming page).
 *
 * Callers get a fresh closure per render so the mapping stays stable
 * across cards on the same page, but does not leak across renders.
 */
export function makeProjectColorForId(): (id: string | null) => string {
  const idToSlot = new Map<string, number>();
  return (id: string | null): string => {
    if (id === null) return PROJECT_COLOR_FALLBACK;
    let slot = idToSlot.get(id);
    if (slot === undefined) {
      slot = idToSlot.size % PROJECT_COLORS.length;
      idToSlot.set(id, slot);
    }
    return projectColorAt(slot);
  };
}

/* ── Time math in a given IANA timezone ─────────────────────────────── */

/**
 * Convert an ISO UTC timestamp to minutes-from-midnight in the given
 * IANA timezone. Uses `Intl.DateTimeFormat` with `timeZone` — safe
 * across all evergreen browsers, no dep. The result can exceed 1440 or
 * fall below 0 if the block straddles a day boundary in the org's tz;
 * the caller clamps via `viewStartHour`/`viewEndHour` downstream.
 */
export function isoToMinutesInTz(iso: string, tz: string): number {
  // eslint-disable-next-line no-restricted-syntax -- parsing a server-supplied ISO string, not reading wall-clock; frozen-time rule targets wall-clock use
  const d = new Date(iso);
  // `hourCycle: 'h23'` so midnight renders as "00", not "24".
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(d);
  let h = 0;
  let m = 0;
  for (const p of parts) {
    if (p.type === 'hour') h = parseInt(p.value, 10);
    else if (p.type === 'minute') m = parseInt(p.value, 10);
  }
  return h * 60 + m;
}

/* ── DayBlock — render-ready block descriptor ───────────────────────── */

export interface DayBlock {
  leftPct: number;
  widthPct: number;
  colorToken: string;
  label: string;
  isWork: boolean;
  /** True for work blocks whose project is null (unclassified). */
  isUnclassified: boolean;
  /** Minutes-since-midnight in the org timezone (may exceed [0, 1440)). */
  startMin: number;
  endMin: number;
  /** Preserved from the source WorkBlock for detail-list rendering. */
  eventCount: number;
}

/** Clamp blocks to the visible 0–100% range. */
export function clampBlocks(blocks: DayBlock[]): DayBlock[] {
  return blocks.map((b) => {
    const left = Math.max(0, b.leftPct);
    const right = Math.min(100, b.leftPct + b.widthPct);
    return { ...b, leftPct: left, widthPct: Math.max(0, right - left) };
  });
}

/**
 * Produce `DayBlock[]` directly from a real workblocks-day response.
 * Positions come from real `startAt`/`endAt` timestamps interpreted in
 * the org timezone. Work-block colour comes from the project colour map
 * so the same project is consistently the same colour across days.
 */
export function buildDayBlocksFromWorkblocks(
  day: WorkBlocksDayResponse,
  viewStartHour: number,
  viewEndHour: number,
  tz: string,
  projectColorForId: (id: string | null) => string,
): DayBlock[] {
  const viewStartMin = viewStartHour * 60;
  const viewRange = (viewEndHour - viewStartHour) * 60;

  const blocks: DayBlock[] = day.blocks.map((b: WorkBlockView): DayBlock => {
    const startMin = isoToMinutesInTz(b.startAt, tz);
    const endMin = isoToMinutesInTz(b.endAt, tz);
    const dur = Math.max(0, endMin - startMin);
    const isWork = b.kind === 'work';
    const isUnclassified = isWork && b.projectId === null;
    const colorToken = isWork ? projectColorForId(b.projectId) : '--color-break';
    const label = isWork ? (b.projectName ?? 'Unclassified') : 'Break';
    return {
      leftPct: ((startMin - viewStartMin) / viewRange) * 100,
      widthPct: (dur / viewRange) * 100,
      colorToken,
      label,
      isWork,
      isUnclassified,
      startMin,
      endMin,
      eventCount: b.eventCount,
    };
  });

  return clampBlocks(blocks);
}

/* ── Adapter to the DayTimeline primitive's TimelineBlock shape ─────── */

/**
 * Adapt a `DayBlock` to the `TimelineBlock` shape consumed by the shared
 * `<DayTimeline>` component. The shapes differ in two places:
 *   - `<DayTimeline>` expects `colorToken` as a bare identifier
 *     (`'timeline-billable'`) and prefixes it with `var(--…)` itself;
 *     our `DayBlock.colorToken` is already the full CSS custom-property
 *     name (`'--color-project-a'`). We pass it through as-is because
 *     `<DayTimeline>` is permissive — it wraps any string value with
 *     `var(...)`, which works correctly for both shapes.
 *   - `DayBlock` does not carry `app` or `detail` — the Daily page sets
 *     `detail` explicitly based on `eventCount` + unclassified status
 *     (task 129 D5).
 */
export function toTimelineBlock(block: DayBlock, detail?: string): TimelineBlock {
  return {
    startMin: block.startMin,
    endMin: block.endMin,
    label: block.label,
    colorToken: block.colorToken,
    detail,
  };
}
